/**
 * Terminal 82 - Intraday Chart Component
 *
 * Creates the 24-hour price line chart with volume bars.
 * ONLY renders with real data - no fake/synthetic data allowed.
 */

import { normalizeVolumeData, getVolumeIntensityClass } from './utils';

// ============================================
// SVG Path Generation
// ============================================

/**
 * Generate SVG path from price data
 * @param prices - Array of 24 hourly prices
 * @param width - SVG viewBox width
 * @param height - SVG viewBox height
 */
export function generatePricePath(
  prices: number[],
  width = 180,
  height = 32
): string {
  // Handle edge cases
  if (prices.length === 0) {
    return `M0,${height / 2} L${width},${height / 2}`;
  }
  if (prices.length === 1) {
    const y = height / 2;
    return `M0,${y} L${width},${y}`;
  }

  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  const divisor = prices.length - 1;

  const points = prices.map((price, i) => {
    const x = (i / divisor) * width;
    const y = height - ((price - min) / range) * (height - 4) - 2;
    return { x, y };
  });

  return points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(' ');
}

// ============================================
// Intraday Chart HTML
// ============================================

export interface IntradayChartOptions {
  prices: number[];
  volumes?: number[];
  direction?: 'bullish' | 'bearish';
}

/**
 * Create intraday chart HTML
 * Only renders price line if prices provided
 * Only renders volume bars if volumes provided
 */
export function createIntradayChart(options: IntradayChartOptions): string {
  const { prices, volumes } = options;

  // Check if we have real data
  const hasRealPrices = prices && prices.length > 0;
  const hasRealVolumes = volumes && volumes.length > 0;

  // If no real data at all, show placeholder
  if (!hasRealPrices && !hasRealVolumes) {
    return `
    <div class="intraday-block">
      <div class="intraday-header">
        <span class="intraday-title">24H INTRADAY</span>
      </div>
      <div class="intraday-chart intraday-no-data">
        <span class="no-data-label">NO DATA</span>
      </div>
    </div>
  `;
  }

  const path = hasRealPrices ? generatePricePath(prices) : '';
  const volumeBars = hasRealVolumes ? createVolumeBars(volumes) : '';

  return `
    <div class="intraday-block">
      <div class="intraday-header">
        <span class="intraday-title">24H INTRADAY</span>
      </div>
      <div class="intraday-chart">
        ${hasRealPrices ? `
        <svg viewBox="0 0 180 32" preserveAspectRatio="none">
          <path class="price-line" d="${path}" />
        </svg>
        ` : '<div class="no-data-label">NO PRICE DATA</div>'}
      </div>
      <div class="time-axis">
        <span>00</span><span>06</span><span>12</span><span>18</span><span>24</span>
      </div>
      ${hasRealVolumes ? `
      <div class="volume-row">
        ${volumeBars}
      </div>
      ` : ''}
    </div>
  `;
}

// ============================================
// Volume Bars
// ============================================

/**
 * Create volume bar HTML
 */
function createVolumeBars(volumes: number[]): string {
  const normalized = normalizeVolumeData(volumes);

  return normalized
    .map((height) => {
      const intensity = getVolumeIntensityClass(height);
      return `<div class="vol-bar ${intensity}" style="height: ${height}%"></div>`;
    })
    .join('\n        ');
}

/**
 * Update intraday chart with new data
 */
export function updateIntradayChart(
  container: HTMLElement,
  prices: number[]
): void {
  const path = container.querySelector('.price-line');
  if (path) {
    path.setAttribute('d', generatePricePath(prices));
  }
}
